import {
  Component,
  inject,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import { Observable, filter, Subscription } from 'rxjs';
import { Store } from '@ngrx/store';

import {
  ButtonMode,
  ButtonTheme,
  ButtonType,
  InputType,
  LoaderTheme,
  ModalButtons,
  NotificationButtonStyle,
  NotificationLayout,
  NotificationTheme,
  TabTheme,
} from '@fcom/ui-components';
import { finShare } from '@fcom/rx';
import { AppState, mapError } from '@fcom/core';
import { Profile } from '@fcom/core-api/login';
import { profile } from '@fcom/core/selectors';

import {
  TwoFactorAuthService,
  TwoFactorAuthStatus,
  Validate2faEmailVerificationCodeErrorResponse,
  isValidate2faEmailVerificationCodeErrorResponseWithCode,
} from '../../services/two-factor-auth.service';
import {
  EmailConfirmErrorCode,
  EmailVerificationResult,
} from '../../interfaces/loyalty-email-change-verification-mfa-form.interface';
import { GtmService } from '../../../gtm';
import { ElementActions, ElementTypes, GaContext } from '../../..';

export enum AuthError {
  Resend2faEmail = 'resend2faEmail',
  ValidateCode = 'validateCode',
  IncorrectCode = 'incorrectCode',
}

const MAX_RESEND_ATTEMPTS = 2;

@Component({
  selector: 'fin-loyalty-email-change-verification-mfa-form',
  templateUrl: './loyalty-email-change-verification-mfa-form.component.html',
  styleUrls: ['./loyalty-email-change-verification-mfa-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class LoyaltyEmailChangeVerificationMfaFormComponent implements OnInit, OnDestroy, OnChanges {
  private readonly fb = inject(UntypedFormBuilder);
  private readonly store$ = inject<Store<AppState>>(Store);
  private gtmService = inject(GtmService);
  twoFactorAuthService = inject(TwoFactorAuthService);

  readonly ButtonTheme = ButtonTheme;
  readonly ButtonType = ButtonType;
  readonly TabTheme = TabTheme;
  readonly TwoFactorAuthStatus = TwoFactorAuthStatus;
  readonly ModalButtons = ModalButtons;
  readonly NotificationTheme = NotificationTheme;
  readonly NotificationLayout = NotificationLayout;
  readonly NotificationButtonStyle = NotificationButtonStyle;
  readonly LoaderTheme = LoaderTheme;
  readonly InputType = InputType;
  readonly ButtonMode = ButtonMode;
  readonly AuthError = AuthError;

  profile$: Observable<Profile>;
  authError: AuthError = undefined;
  resendEmailCount = 0;
  showSend2faEmailVerificationCodeSuccessNotification = false;

  verificationForm: UntypedFormGroup;

  @Input() inModalLayout = false;
  /** Will have an effect when toggled to true only if newEmail input is not provided. */
  @Input() forceSetEmailMfa = false;
  @Input() newEmail?: string;
  @Input() submitEmailVerification$: Observable<void>;
  @Input() trackElementNamePrefix: string;
  @Input() trackExtraEmailVerificationCodeEvents = false;

  @Output() emailVerificationResult = new EventEmitter<EmailVerificationResult>();

  private submitEmailVerificationSubscription?: Subscription;
  private requestSubscription?: Subscription;

  ngOnInit(): void {
    this.profile$ = this.store$.pipe(profile(), filter(Boolean), finShare());
    this.verificationForm = this.fb.group({
      verificationCode: this.fb.control(
        '',
        Validators.compose([
          Validators.required,
          Validators.minLength(6),
          Validators.maxLength(6),
          Validators.pattern(/^\d+$/),
        ])
      ),
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['submitEmailVerification$']) {
      this.setupSubmitEmailVerificationSubscription();
    }
  }

  private setupSubmitEmailVerificationSubscription(): void {
    if (this.submitEmailVerificationSubscription) {
      this.submitEmailVerificationSubscription.unsubscribe();
      this.submitEmailVerificationSubscription = undefined;
    }

    if (this.submitEmailVerification$) {
      this.submitEmailVerificationSubscription = this.submitEmailVerification$.subscribe(() => {
        this.verifyCode();
      });
    }
  }

  ngOnDestroy(): void {
    this.submitEmailVerificationSubscription?.unsubscribe();
    this.requestSubscription?.unsubscribe();
  }

  send2faEmailVerificationCode(): void {
    this.showSend2faEmailVerificationCodeSuccessNotification = false;
    this.authError = undefined;

    this.resendEmailCount++;

    if (this.resendEmailCount > MAX_RESEND_ATTEMPTS) {
      return;
    }

    if (this.trackExtraEmailVerificationCodeEvents) {
      this.gtmService.trackElement(
        `${this.trackElementNamePrefix}modal`,
        GaContext.FINNAIR_PLUS,
        ElementTypes.BUTTON,
        'resend-code',
        ElementActions.CLICK
      );

      if (this.resendEmailCount === 2) {
        this.gtmService.trackElement(
          `${this.trackElementNamePrefix}error`,
          GaContext.FINNAIR_PLUS,
          ElementTypes.NOTIFICATION,
          'still-no-code',
          ElementActions.VIEW
        );
      }
    }

    this.requestSubscription?.unsubscribe();

    if (this.newEmail) {
      this.requestSubscription = this.twoFactorAuthService
        .initiateEmailChange(this.newEmail)
        .subscribe(this.getEmailFlowObserver());
    } else {
      this.requestSubscription = this.twoFactorAuthService
        .send2faEmailVerificationCode()
        .subscribe(this.getEmailFlowObserver());
    }
  }

  verifyCode(): void {
    this.showSend2faEmailVerificationCodeSuccessNotification = false;
    this.authError = undefined;

    this.gtmService.trackElement(
      `${this.trackElementNamePrefix}verify-email`,
      GaContext.FINNAIR_PLUS,
      ElementTypes.BUTTON,
      undefined,
      ElementActions.CLICK
    );

    if (this.validateVerificationFormCode() === 'invalid') {
      return;
    }

    const verificationCode = this.verificationForm.get('verificationCode').value;
    this.requestSubscription?.unsubscribe();

    this.requestSubscription = this.twoFactorAuthService
      .validate2faEmailVerificationCode(verificationCode, this.forceSetEmailMfa, this.newEmail)
      .subscribe({
        next: (response) => {
          this.emailVerificationResult.emit({ success: true, memberEmailChangedTo: response.memberEmailChangedTo });
        },
        error: (error: unknown) => {
          const errorResponseData = mapError<Validate2faEmailVerificationCodeErrorResponse>(error);

          let emailVerificationErrorCode: EmailConfirmErrorCode;

          if (isValidate2faEmailVerificationCodeErrorResponseWithCode(errorResponseData)) {
            const isIncorrectCode = errorResponseData.error.code === EmailConfirmErrorCode.INVALID_OR_EXPIRED_TOKEN;

            this.authError = isIncorrectCode ? AuthError.IncorrectCode : AuthError.ValidateCode;
            emailVerificationErrorCode = errorResponseData.error.code;

            if (isIncorrectCode && this.trackExtraEmailVerificationCodeEvents) {
              this.gtmService.trackElement(
                `${this.trackElementNamePrefix}error`,
                GaContext.FINNAIR_PLUS,
                ElementTypes.NOTIFICATION,
                'code-incorrect',
                ElementActions.VIEW
              );
            }
          }

          this.authError = this.authError || AuthError.ValidateCode;
          this.emailVerificationResult.emit({ success: false, errorCode: emailVerificationErrorCode });
        },
      });
  }

  onCloseError(): void {
    this.authError = undefined;
  }

  private validateVerificationFormCode(): 'valid' | 'invalid' {
    if (this.verificationForm.invalid) {
      this.verificationForm.markAllAsTouched();

      const validationErrors = this.verificationForm.get('verificationCode').errors;
      const state = validationErrors?.required
        ? 'code-required'
        : validationErrors?.minlength
          ? 'code-min-length'
          : validationErrors?.maxlength
            ? 'code-max-length'
            : 'code-pattern';

      if (this.trackExtraEmailVerificationCodeEvents) {
        this.gtmService.trackElement(
          `${this.trackElementNamePrefix}error`,
          GaContext.FINNAIR_PLUS,
          ElementTypes.NOTIFICATION,
          state,
          ElementActions.VIEW
        );
      }
      return 'invalid';
    }
    return 'valid';
  }

  private getEmailFlowObserver() {
    return {
      next: () => {
        this.showSend2faEmailVerificationCodeSuccessNotification = true;
      },
      error: () => {
        this.authError = AuthError.Resend2faEmail;
      },
    };
  }
}
